import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { TaskService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export interface UseAdminTasksParams {
  sortField?: keyof TaskService.Task
  sortDirection?: 'asc' | 'desc'
  initialPageSize?: number
}

const DEFAULT_PAGE_SIZE = 30

export const adminTasksQueryKeys = {
  all: () => ['admin-tasks'] as const,
  list: (params: { sortField?: keyof TaskService.Task; sortDirection?: 'asc' | 'desc' }) =>
    ['admin-tasks', params] as const,
}

const fetchAdminTasksPage = async ({
  pageParam,
  sortField,
  sortDirection,
}: {
  pageParam: { limit: number; offset: number }
  sortField?: keyof TaskService.Task
  sortDirection?: 'asc' | 'desc'
}): Promise<TaskService.Task[]> => {
  const { limit, offset } = pageParam
  const response = await apis.taskService.rootGet(
    undefined,
    sortField as TaskService.TaskSortFieldEnum | undefined,
    sortDirection as TaskService.SortDirectionEnum | undefined,
    limit,
    offset
  )
  return response.data
}

export const useAdminTasks = ({
  sortField,
  sortDirection,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseAdminTasksParams) => {
  const query = useInfiniteQuery({
    queryKey: adminTasksQueryKeys.list({ sortField, sortDirection }),
    queryFn: ({ pageParam }) =>
      fetchAdminTasksPage({
        pageParam: pageParam ?? { limit: initialPageSize, offset: 0 },
        sortField,
        sortDirection,
      }),
    initialPageParam: { limit: initialPageSize, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length === 0) return undefined
      const currentOffset = allPages.reduce((sum, page) => sum + page.length, 0)
      const nextLimit = allPages.length === 1
        ? initialPageSize * 2
        : Math.min(initialPageSize * 2 ** allPages.length, initialPageSize * 2 ** 8)
      return { limit: nextLimit, offset: currentOffset }
    },
  })

  const tasks = useMemo(() => query.data?.pages.flat() ?? [], [query.data])
  const hasMore = !!query.hasNextPage

  return {
    tasks,
    hasMore,
    isInitialLoading: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}
